import { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Modal, useToast } from '../../components/primitives';
import { AresSearchField, type AresPickResult } from '../../components/ares';
import { api, ApiError } from '../../lib/api';
import { czAccountToIban } from '../../lib/iban';
import { cx } from '../../lib/cx';
import { useAuth, type User } from '../auth/AuthContext';
import {
  EMPTY_FIRMA,
  useDeleteMojeFirma,
  useMojeFirmy,
  useSaveMojeFirma,
  type MojeFirma,
  type MojeFirmaData,
} from './mojeFirmy';
import styles from './Ucet.module.css';

function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.detail && typeof err.detail === 'object' && 'detail' in err.detail) {
    return String((err.detail as { detail: unknown }).detail);
  }
  return fallback;
}

function toData(f: MojeFirma): MojeFirmaData {
  const { id: _id, poradi: _p, ...data } = f;
  return { ...EMPTY_FIRMA, ...data };
}

/**
 * Nastavení účtu: jméno uživatele + „Moje firmy" — vlastní fakturační údaje
 * (víc firem, přepínání záložkami, ARES, banka, pobočka s jinou adresou).
 * `initialFirmaId` = otevřít rovnou danou firmu (z „Moje údaje" → Upravit).
 */
export function AccountSettingsModal({
  open,
  onClose,
  initialFirmaId,
}: {
  open: boolean;
  onClose: () => void;
  initialFirmaId?: number | null;
}) {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const firmyQuery = useMojeFirmy(open);
  const saveFirma = useSaveMojeFirma();
  const deleteFirma = useDeleteMojeFirma();
  const firmy = firmyQuery.data ?? [];

  const [name, setName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  // 'new' = nová (neuložená) firma, undefined = ještě nevybráno (po otevření)
  const [selected, setSelected] = useState<number | 'new' | undefined>(undefined);
  const [draft, setDraft] = useState<MojeFirmaData>(EMPTY_FIRMA);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedId = typeof selected === 'number' ? selected : null;

  useEffect(() => {
    if (!open) return;
    setName(user?.full_name ?? '');
    setError(null);
  }, [open, user?.full_name]);

  // Po otevření a načtení vyber požadovanou / první firmu, jinak rovnou formulář nové.
  useEffect(() => {
    if (!open || !firmyQuery.isSuccess || selected !== undefined) return;
    const pick = firmy.find((f) => f.id === initialFirmaId) ?? firmy[0];
    setSelected(pick ? pick.id : 'new');
    setDraft(pick ? toData(pick) : EMPTY_FIRMA);
  }, [open, firmyQuery.isSuccess, firmy, initialFirmaId, selected]);

  function close() {
    if (dirty && !window.confirm('Máte neuložené změny firmy. Zavřít bez uložení?')) return;
    setDirty(false);
    setSelected(undefined);
    onClose();
  }

  function selectFirma(f: MojeFirma | null) {
    if (dirty && !window.confirm('Máte neuložené změny firmy. Přepnout bez uložení?')) return;
    setError(null);
    setDirty(false);
    setSelected(f ? f.id : 'new');
    setDraft(f ? toData(f) : EMPTY_FIRMA);
  }

  function set<K extends keyof MojeFirmaData>(key: K, value: MojeFirmaData[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  }

  function applyAres(a: AresPickResult) {
    setDraft((d) => ({
      ...d,
      nazev: a.nazev || d.nazev,
      ico: a.ico || d.ico,
      dic: a.dic || d.dic,
      adresa: a.adresa || d.adresa,
    }));
    setDirty(true);
  }

  function fillIban() {
    if (draft.banka_iban.trim() || !draft.banka_ucet.trim()) return;
    const iban = czAccountToIban(draft.banka_ucet);
    if (iban) set('banka_iban', iban);
  }

  async function saveName() {
    setNameSaving(true);
    try {
      const u = await api.put<User>('/auth/me', { full_name: name });
      updateUser(u);
      toast.success('Jméno uloženo.');
    } catch (err) {
      toast.error(apiErrorText(err, 'Jméno se nepodařilo uložit.'));
    } finally {
      setNameSaving(false);
    }
  }

  async function handleSave() {
    setError(null);
    if (!draft.nazev.trim()) {
      setError('Vyplňte název firmy.');
      return;
    }
    try {
      const saved = await saveFirma.mutateAsync({ id: selectedId, data: draft });
      setDirty(false);
      setSelected(saved.id);
      setDraft(toData(saved));
      toast.success('Firma uložena.');
    } catch (err) {
      setError(apiErrorText(err, 'Firmu se nepodařilo uložit.'));
    }
  }

  async function handleDelete() {
    if (selectedId === null) {
      // Zahodit rozepsanou novou firmu
      setDirty(false);
      setError(null);
      setSelected(firmy[0]?.id ?? 'new');
      setDraft(firmy[0] ? toData(firmy[0]) : EMPTY_FIRMA);
      return;
    }
    if (!window.confirm(`Opravdu odebrat firmu „${draft.nazev}" z vašich údajů?`)) return;
    try {
      await deleteFirma.mutateAsync(selectedId);
      setDirty(false);
      const rest = firmy.filter((f) => f.id !== selectedId);
      setSelected(rest[0]?.id ?? 'new');
      setDraft(rest[0] ? toData(rest[0]) : EMPTY_FIRMA);
      toast.success('Firma odebrána.');
    } catch (err) {
      setError(apiErrorText(err, 'Firmu se nepodařilo odebrat.'));
    }
  }

  const [prefsSaving, setPrefsSaving] = useState(false);
  async function savePrefs(patch: { ui_theme?: 'dark' | 'light'; ui_doc_white?: boolean }) {
    setPrefsSaving(true);
    try {
      const u = await api.put<User>('/auth/me/prefs', patch);
      updateUser(u);
    } catch (err) {
      toast.error(apiErrorText(err, 'Nastavení vzhledu se nepodařilo uložit.'));
    } finally {
      setPrefsSaving(false);
    }
  }

  const nameChanged = name.trim() !== (user?.full_name ?? '') && !!name.trim();

  return (
    <Modal open={open} onClose={close} title="⚙ Nastavení účtu" size="lg" closeOnBackdrop={false}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Uživatel</h3>
        <div className={styles.row}>
          <Input label="Jméno a příjmení" compact value={name} onChange={(e) => setName(e.target.value)} />
          <Button onClick={saveName} disabled={!nameChanged || nameSaving}>
            {nameSaving ? 'Ukládám…' : 'Uložit jméno'}
          </Button>
        </div>
        <div className={styles.muted}>Přihlašovací e-mail: {user?.email}</div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Vzhled</h3>
        <Checkbox
          label="Světlý režim aplikace (bílé menu a panely místo tmavě modrých)"
          checked={user?.ui_theme === 'light'}
          disabled={prefsSaving}
          onChange={(e) => savePrefs({ ui_theme: e.target.checked ? 'light' : 'dark' })}
        />
        <Checkbox
          label="Bílé dokumenty (popisky a tabulky čistě bílé místo béžových, i v PDF)"
          checked={!!user?.ui_doc_white}
          disabled={prefsSaving}
          onChange={(e) => savePrefs({ ui_doc_white: e.target.checked })}
        />
        <div className={styles.muted}>Ukládá se k účtu — platí na všech zařízeních.</div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Moje firmy — fakturační údaje</h3>
        <div className={styles.muted}>
          Firmy, za které jednáte. Údaje pak rychle zobrazíte a zkopírujete tlačítkem 🏢 Moje údaje v
          hlavičce.
        </div>

        {firmyQuery.isError && <div className={styles.error}>Firmy se nepodařilo načíst.</div>}

        <div className={styles.pills} role="tablist" aria-label="Moje firmy">
          {firmy.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={selectedId === f.id}
              className={cx(styles.pill, selectedId === f.id && styles.pillActive)}
              onClick={() => selectedId !== f.id && selectFirma(f)}
              title={f.nazev}
            >
              {f.nazev || 'Bez názvu'}
            </button>
          ))}
          {selectedId === null && firmy.length > 0 && (
            <span className={cx(styles.pill, styles.pillActive)}>Nová firma</span>
          )}
          {selectedId !== null && (
            <button type="button" className={cx(styles.pill, styles.pillAdd)} onClick={() => selectFirma(null)}>
              + Přidat firmu
            </button>
          )}
        </div>

        <AresSearchField
          label="Název firmy"
          placeholder="Pište název pro vyhledání v ARES"
          required
          value={draft.nazev}
          onChange={(v) => set('nazev', v)}
          ico={draft.ico}
          onIcoChange={(v) => set('ico', v)}
          onPick={applyAres}
          hint="Napište název (našeptávač) nebo zadejte IČO a stiskněte 🔍 ARES"
        />
        <div className={styles.grid2}>
          <Input label="DIČ" mono compact value={draft.dic} onChange={(e) => set('dic', e.target.value)} />
          <Input
            label="Zápis v obchodním rejstříku"
            compact
            placeholder="C 123456 vedená u MS v Praze"
            value={draft.zapis_or}
            onChange={(e) => set('zapis_or', e.target.value)}
          />
        </div>
        <Input label="Sídlo" compact value={draft.adresa} onChange={(e) => set('adresa', e.target.value)} />

        <h4 className={styles.subhead}>Bankovní spojení</h4>
        <div className={styles.grid2}>
          <Input
            label="Číslo účtu"
            mono
            compact
            placeholder="123456789/0100"
            value={draft.banka_ucet}
            onChange={(e) => set('banka_ucet', e.target.value)}
            onBlur={fillIban}
          />
          <Input label="Banka" compact value={draft.banka_nazev} onChange={(e) => set('banka_nazev', e.target.value)} />
          <Input
            label="IBAN"
            mono
            compact
            hint="Doplní se z čísla účtu"
            value={draft.banka_iban}
            onChange={(e) => set('banka_iban', e.target.value)}
          />
          <Input label="SWIFT / BIC" mono compact value={draft.banka_swift} onChange={(e) => set('banka_swift', e.target.value)} />
        </div>

        <h4 className={styles.subhead}>Pobočka / provozovna (jiná adresa než sídlo)</h4>
        <div className={styles.grid2}>
          <Input
            label="Název pobočky"
            compact
            placeholder="Provozovna Praha 9"
            value={draft.pobocka_nazev}
            onChange={(e) => set('pobocka_nazev', e.target.value)}
          />
          <Input
            label="Adresa pobočky"
            compact
            value={draft.pobocka_adresa}
            onChange={(e) => set('pobocka_adresa', e.target.value)}
          />
        </div>

        <h4 className={styles.subhead}>Kontakt</h4>
        <div className={styles.grid2}>
          <Input label="E-mail" type="email" compact value={draft.email} onChange={(e) => set('email', e.target.value)} />
          <Input label="Telefon" type="tel" compact value={draft.telefon} onChange={(e) => set('telefon', e.target.value)} />
          <Input label="Web" compact value={draft.web} onChange={(e) => set('web', e.target.value)} />
          <Input label="Poznámka" compact value={draft.poznamka} onChange={(e) => set('poznamka', e.target.value)} />
        </div>

        {error && (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}

        <div className={styles.formActions}>
          <button type="button" className={styles.dangerLink} onClick={handleDelete}>
            {selectedId === null ? 'Zahodit novou firmu' : 'Odebrat tuto firmu'}
          </button>
          <Button variant="primary" onClick={handleSave} disabled={saveFirma.isPending || (!dirty && selectedId !== null)}>
            {saveFirma.isPending ? 'Ukládám…' : selectedId === null ? 'Přidat firmu' : 'Uložit firmu'}
          </Button>
        </div>
      </section>
    </Modal>
  );
}
